import * as React from "react";
import { useMappedState } from "redux-react-hook";
import { useHistory } from "react-router-dom";
import _, { capitalize } from "lodash";
import { toast } from "react-toastify";

import {
  ActionButton,
  Button
} from "src/library/components/Button";
import { LikeToDo } from "src/library/components/LikeToDo";
import { ButtonLink } from "src/library/components/Link";
import { Modal } from "src/library/components/Modal";
import Table, { ITableInstance } from "src/library/components/Tables/Table";
import { IAction } from "src/library/components/Button/ActionButton";
import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import { ImportModal } from "src/core/shared/components/ImportModal";
import { customersTableColumns } from "src/core/shared/components/tables";
import {
  ERPSeedless,
  ERPRJL,
  ERPUnity,
  ERPOdoo
} from "src/utils/constants";
import {
  CustomerDropStatuses,
  ICustomer,
  useCustomerApi
} from "src/core/customers/api/customers.api";
import {
  FilterParams,
  PaginatedParams
} from "src/core/shared/api";
import type { IStore } from "src/core/shared/store";
import { PageProps } from "src/pages/Router/routes";
import { CustomerDropsExportModal } from "src/core/customers/components/CustomerDropsExportModal";
import useCheckUserPermission from "src/core/users/hooks/useCheckUserPermission";
import { ConnectorCustomerImportModal } from "src/modules/connector/components/ConnectorCustomerImportModel";
import { useConnectorApi } from "src/modules/connector/api/connector.api";
import { Text } from "src/library/components/Text";
import {
  FormRow,
  Input,
  Radio
} from "src/library/components/Forms";
import {
  ImportOrigin,
  ImportSettingType,
  useImportSettingApi
} from "src/core/shared/api/importSetting.api";

const defaultPagination: PaginatedParams<ICustomer> = {
  page: 0,
  limit: 20,
  offset: 0,
  sort: [ [ "name", "ASC" ] ]
};

/**
 * Main "Customers" View
 *
 * @returns
 */
const CustomersPage: React.ReactFCWithChildren<PageProps<unknown>> = props => {
  const { terminology } = props;
  const history = useHistory();
  const TableRef = React.useRef<ITableInstance<ICustomer>>(null);
  const [ showImportModal, setShowImportModal ] = React.useState(false);
  const [ showERPImportModal, setShowERPImportModal ] = React.useState(false);
  const [ showExportModal, setShowExportModal ] = React.useState(false);
  const [ customersResponse, getCustomers ] = useCustomerApi("LIST_CUSTOMERS");
  const [ updateCustomerStatusResponse, updateCustomerStatus ] = useCustomerApi("UPDATE_CUSTOMER_STATUS");
  const [ suspendModalState, setSuspendModalState ] = React.useState<ICustomer & { newStatus: CustomerDropStatuses.Deactivated | CustomerDropStatuses.Suspended } | null>(null);
  const [ shouldCancelOrders, setShouldCancelOrders ] = React.useState(false);
  const [ syncCustomerResponse, syncCustomerRequest ] = useConnectorApi("IMPORT_API_CUSTOMER");
  const [ triggerFtpImportResponse, triggerFtpImportRequest ] = useImportSettingApi("TRIGGER_FTP_IMPORT");

  const { erp, importSettings } = useMappedState(({ global }: IStore) => ({
    erp: global.settings && global.settings.erp.name,
    importSettings: global.settings?.importSettings
  }));

  const checkPermission = useCheckUserPermission();

  const canCreateCustomer = React.useMemo(() => {
    return checkPermission("create-customer");
  }, [ checkPermission ]);

  React.useEffect(() => {
    if (triggerFtpImportResponse.data) {
      toast.success(triggerFtpImportResponse.data.message);
    } else if (triggerFtpImportResponse.error) {
      toast.error(triggerFtpImportResponse.error.message);
    }
  }, [ triggerFtpImportResponse.data, triggerFtpImportResponse.error ]);

  const onFetchData = React.useCallback((filters: FilterParams<ICustomer>, pagination: PaginatedParams<ICustomer>) => {
    if (!filters[ "status" ]) [ filters[ "status" ] = { $not: CustomerDropStatuses.Deactivated } ];

    getCustomers({
      pagination,
      filters
    });
  }, [ getCustomers ]);

  React.useEffect(() => {
    if ((updateCustomerStatusResponse.data && TableRef.current)) {
      TableRef.current.reloadTable();
      toast.success(updateCustomerStatusResponse.data.message);
    } else if (updateCustomerStatusResponse.error) {
      toast.error(updateCustomerStatusResponse.error.message);
    }
  }, [ updateCustomerStatusResponse.data, updateCustomerStatusResponse.error ]);

  const CreateNewCustomerButton = React.useMemo(() => (
    <ButtonLink
      to="/admin/customers/create"
      size="large"
    >
      {`Create a new ${terminology.customer.singular.text}`}
    </ButtonLink>
  ), [ terminology.customer.singular.text ]);

  const returnActions = (row: any) => {
    const canAmendCustomer = checkPermission("amend-customer");

    const actions: IAction[] = [
      {
        title: `View ${terminology.drop.plural.text}`,
        action: () => {
          history.push(`/admin/customers/${row.original.id}/drops`);
        },
        newTabPath: `/admin/customers/${row.original.id}/drops`
      },
      {
        title: `View ${terminology.order.singular.text} settings`,
        action: () => {
          history.push(`/admin/customers/${row.original.id}/defaults`);
        },
        newTabPath: `/admin/customers/${row.original.id}/defaults`
      },
      {
        title: `View ${terminology.user.plural.text}`,
        action: () => {
          history.push(`/admin/customers/${row.original.id}/users`);
        },
        newTabPath: `/admin/customers/${row.original.id}/users`
      },
      {
        title: `${(erp === ERPSeedless || erp === ERPRJL) && canAmendCustomer ? "Edit" : "View"} Contact Details`,
        action: () => {
          history.push(`/admin/customers/${row.original.id}/contact-details`);
        },
        newTabPath: `/admin/customers/${row.original.id}/contact-details`
      }
    ];

    if (erp === ERPUnity && canCreateCustomer) {
      actions.push({
        title: `Import from ${capitalize(erp)}`,
        action: () => {
          syncCustomerRequest({ customerCode: row.original.accountNumber });
        }
      });
    }

    if ((erp === ERPSeedless || erp === ERPRJL) && canAmendCustomer) {
      if (row.original.status === "active") {
        actions.push({
          title: "Suspend",
          action: () => {
            setSuspendModalState({
              ...row.original,
              newStatus: "suspended"
            });
          }
        });
      }

      if (row.original.status !== "active") {
        actions.push({
          title: "Activate",
          action: () => {
            // Update the customer
            updateCustomerStatus({
              cancelOrders: shouldCancelOrders,
              customerId: row.original.id,
              status: CustomerDropStatuses.Active
            });
          }
        });
      }

      if ([
        CustomerDropStatuses.Active,
        CustomerDropStatuses.OverCreditLimit,
        CustomerDropStatuses.Suspended
      ].includes(row.original.status)) {
        actions.push({
          // eslint-disable-next-line react/jsx-one-expression-per-line
          title: (<Text color="error">Deactivate</Text>),
          action: () => {
            setSuspendModalState({
              ...row.original,
              newStatus: CustomerDropStatuses.Deactivated
            });
          }
        });
      }
    }

    return actions;
  };

  /**
   * Action buttons to display at the top of the page
   */
  const LikeToDoActions: React.ReactElement[] = React.useMemo(() => {
    const actions: React.ReactElement[] = [
      <Button
        key="export-customers"
        onClick={() => setShowExportModal(true)}
      >
        Export Customers
      </Button>
    ];

    const hasCustomerCSVImportSetting = _.find(importSettings, is => is.type === ImportSettingType.Customer && is.origin === ImportOrigin.CSV);

    if (hasCustomerCSVImportSetting && canCreateCustomer) {
      actions.unshift(
        <Button
          key="import-customers"
          onClick={() => setShowImportModal(true)}
        >
          Import Customers
        </Button>);

      if (erp !== ERPUnity) {
        actions.unshift(CreateNewCustomerButton);
      }
    }
    const hasCustomerFTPImportSetting = _.find(importSettings, is => is.type === ImportSettingType.Customer && is.origin === ImportOrigin.SFTP);

    if (hasCustomerFTPImportSetting && canCreateCustomer) {
      actions.push(
        <Button
          key="import-customers"
          onClick={() => triggerFtpImportRequest(hasCustomerFTPImportSetting.id)}
        >
          Sync customers from FTP
        </Button>);
    }

    // currently only allow unity and odoo to import individual customer
    if (erp === ERPUnity || erp === ERPOdoo && canCreateCustomer) {
      actions.unshift(
        <Button
          key="import-customers"
          onClick={() => setShowERPImportModal(true)}
        >
          {`Import From ${capitalize(erp)}`}
        </Button>);
    }

    return actions;
  }, [
    CreateNewCustomerButton,
    canCreateCustomer,
    erp,
    importSettings,
    triggerFtpImportRequest
  ]);

  /** Handle the api response for the api sync customer action */
  React.useEffect(() => {
    if (syncCustomerResponse.data) {
      toast.success("Customer import has been added to the job queue and will be processed shortly");
    } else if (syncCustomerResponse.error) {
      toast.error(syncCustomerResponse.error.message);
    }
  }, [ syncCustomerResponse ]);

  /** Handles the form content of the suspend/deactivate modal */
  const SuspendOrDeactivateContent = React.useMemo(() => (
    <div>
      <FormRow>
        <Input
          name="statusReason"
          as="input"
          label="Reason"
          handleChange={e => setSuspendModalState(current => {
            if (current) {
              current[ "statusReason" ] = e.target.value;
            }

            return current;
          })}
        />
      </FormRow>

      <FormRow>
        <Radio
          label="Do you want to automatically cancel all future orders for this customer"
          name="cancel-orders"
          handleChange={e => {
            if (e.target.value === "cancel") {
              setShouldCancelOrders(true);
            } else {
              setShouldCancelOrders(false);
            }
          }}
          options={[
            {
              text: "Cancel Orders",
              value: "cancel",
              checked: shouldCancelOrders
            },
            {
              text: "Keep existing orders",
              value: "",
              checked: !shouldCancelOrders
            }
          ]}
        />

      </FormRow>
    </div>
  ), [ shouldCancelOrders ]);

  return (
    <Layout {...props}>
      <MainContentWrapper
        fill
        Header={(
          <LikeToDo
            copy="What would you like to do?"
            buttons={LikeToDoActions}
          />
        )}
      >

        <Table
          tableId="global"
          ref={TableRef}
          tableOf={terminology.customer.plural.text}
          data={customersResponse.data ? customersResponse.data.result : []}
          columns={customersTableColumns(row => (
            <ActionButton
              title="Actions"
              items={returnActions(row)}
              loading={syncCustomerResponse.pending || updateCustomerStatusResponse.pending}
            />
          ))}
          onFetchData={onFetchData}
          mainAction={row => history.push(`/admin/customers/${row.original.id}/drops`)}
          error={customersResponse.error}
          totalRows={customersResponse.data ? customersResponse.data.count : 0}
          pages={customersResponse.data ? customersResponse.data.pages : 0}
          loading={customersResponse.pending}
          defaultPagination={defaultPagination}
        />
      </MainContentWrapper>

      <ImportModal
        open={showImportModal}
        onDismiss={() => setShowImportModal(false)}
        type="customer"
        terminology={props.terminology}
      />

      <CustomerDropsExportModal
        open={showExportModal}
        onDismiss={() => setShowExportModal(false)}
      />

      <ConnectorCustomerImportModal
        open={showERPImportModal}
        onDismiss={() => setShowERPImportModal(false)}
      />

      <Modal
        show={suspendModalState !== null}
        title="Are you sure?"
        body={suspendModalState ? `This will set ${suspendModalState.name} (#${suspendModalState.id}) as ${suspendModalState.newStatus} and will prevent them from placing any further orders.` : ""}
        content={SuspendOrDeactivateContent}
        type="confirm"
        actions={[
          {
            name: "No, take me back",
            action: () => setSuspendModalState(null),
            $buttonType: "secondary"
          },
          {
            name: `Yes, suspend this ${terminology.customer.singular.text}`,
            action: () => {
              if (suspendModalState) {
                // Update the customer
                updateCustomerStatus({
                  cancelOrders: shouldCancelOrders,
                  customerId: suspendModalState.id,
                  status: suspendModalState.newStatus,
                  statusReason: suspendModalState.statusReason
                });
                // Update the suspendModalState
                setSuspendModalState(null);
              }
            },
            $buttonType: "primary"
          }
        ]}
        backgroundAction={() => setSuspendModalState(null)}
      />
    </Layout>
  );
};

export default CustomersPage;
